import { ContactSummary } from '../contact/contact';
import { Entity, EntityViewFieldConfig } from '../entity';
import { JobViewFilters } from '../job/job.filters';
import { OpportunityViewFilters } from '../opportunities/opportunity.filters';
import { DayRelative, DayShortName } from '../shared/schedule.entity';
import { Allocation } from './allocation';

export enum AllocationViewVisibility {
    Public = 'Public',
    Private = 'Private',
    Shared = 'Shared'
}

export enum AllocationViewType {
    Timeline = 'Timeline', // Day V, Day H, Week H, Week V
    Agenda = 'Agenda',
    Month = 'Month',
    Run = 'Run',
    Runs = 'Runs',
    Empty = 'Empty'
}

export enum AllocationViewOrientation {
    Horizontal = 'Horizontal',
    Vertical = 'Vertical'
}

export enum AllocationViewResourcesRestriction {
    All = 'All',
    None = 'None',
    WorkCategories = 'WorkCategories',
    Zones = 'Zones',
    People = 'People'
}

export enum AllocationViewColorField {
    Priority = 'Priority',
    JobType = 'Job Type',
    WorkCategory = "Work Category",
    Status = "Status"
}

export interface AllocationViewResourcesConstraints {
    show: boolean;

    /** @description treat it as Include, not Restriction anymore. This will replace in the future */
    restriction?: AllocationViewResourcesRestriction;

    /** @description work categories */
    restrictTo?: Entity[];

    /** @description calculated/selected resources which have order + color */
    resources: Entity[];
}


export interface AllocationViewJobsFilters extends Pick<
    Partial<JobViewFilters>,
    'customers'
    | 'billingCompanies'
    | 'jobTypes'
    | 'priorities'
    | 'owners'
    | 'fieldSupervisors'
    | 'labels'
    | 'zones'
    | 'workCategories'
    | 'createdAt'
    | 'dueDate'
    | 'completedDate'
    | 'finalisedDate'
> {
}

export interface AllocationViewOpportunitiesFilters extends Pick<
    Partial<OpportunityViewFilters>,
    'tags'
    | 'account'
    | 'owner'
    | 'jobType'
    | 'source'
    | 'stage'
    | 'zones'
    | 'company'
    | 'amount'
    | 'createdAt'
    | 'expectedClose'
    | 'lastActivityDate'
> {
}


export interface AllocationViewSummary extends Entity {
    system?: boolean;
    type: AllocationViewType;
    isDefault?: boolean;

}

export interface AllocationViewCardFields {
    address: boolean;
    customer: boolean;
    customerWO: boolean;
    siteContact: boolean;
    note?: boolean;
    tags: boolean;
    name: boolean;
    time?: boolean;
    status: boolean;
    color: AllocationViewColorField;
}


export interface AllocationView extends AllocationViewSummary {

    owner: Entity;

    showDays: number; // 1 - 5 days only - Slider
    slotDuration?: number; // 15, 30, 120 - Only supported on Timeline
    timezone?: string;

    visibility?: AllocationViewVisibility;
    sharedWith?: Entity[];

    orientation?: AllocationViewOrientation; // Not supported on Agenda View (DayGrid)
    resourcesEnabled?: boolean; // Only on Agenda Views (ResourceDayGrid)

    mySchedule?: boolean;
    unassigned: AllocationViewResourcesConstraints;
    employees: AllocationViewResourcesConstraints;
    equipments: AllocationViewResourcesConstraints;
    contractors: AllocationViewResourcesConstraints;

    /** @deprecated using "fields" instead  */
    card: AllocationViewCardFields;

    canEdit?: boolean;
    canShare?: boolean;

    /** in minutes */
    autoRefresh?: number;

    showEntitySelector?: boolean;

    showResourceAvatar?: boolean;
    showResourceSuburb?: boolean;
    showResourceSchedulingTags?: boolean;
    showResourceTimezone?: boolean;
    showResourceType?: boolean;

    fields: EntityViewFieldConfig[];
    color?: AllocationViewColorField;

    daysOfWeek?: DayShortName[];
    firstDay?: DayShortName | DayRelative;
    firstScrollTime?: string;

    slotMinTime?: string;
    slotMaxTime?: string;

    jobViewId?: string;
    jobsFilters: AllocationViewJobsFilters;

    opportunityViewId?: string;
    opportunitiesFilters: AllocationViewOpportunitiesFilters;

    isEmbedded?: boolean;

}


export interface AllocationViewRequest {
    viewId: string;
    startDate: Date;
    endDate: Date;
    timezone?: string;
    isEmbedded?: boolean;
}

export interface AllocationViewResponse {
    views: AllocationViewSummary[];
    view: AllocationView;
    allocations: Allocation[];
    resources: ContactSummary[];
    canCreateViews: boolean;
    canManageOtherUsersViews: boolean;
}

export interface AllocationViewsResponse {
    views: AllocationViewSummary[];
    defaultView: AllocationView;
}

export const DefaultAllocationFields: EntityViewFieldConfig[] = [
    { _id: 'number', name: 'Number', visible: true, system: true },
    { _id: 'name', name: 'Name', visible: true, system: true },
    { _id: 'address', name: 'Address', visible: true, system: true },
    { _id: 'suburb', name: 'Suburb', visible: true, system: true },
    { _id: 'customer', name: 'Customer', visible: true, system: true },
    { _id: 'customerWO', name: 'Customer WO', visible: true, system: true },
    { _id: 'siteName', name: 'Site Name', visible: true, system: true },
    { _id: 'siteNote', name: 'Site Note', visible: true, system: true },
    { _id: 'siteReference', name: 'Site Reference', visible: true, system: true },
    { _id: 'siteContact', name: 'Site Contact', visible: true, system: true },
    { _id: 'note', name: 'Note', visible: true, system: true },
    { _id: 'jobTags', name: 'Job Tags', visible: false, system: true },
    { _id: 'tags', name: 'Stage Tags', visible: true, system: true },
    { _id: 'jobType', name: 'Job Type', visible: true, system: true },
    { _id: 'workCategory', name: 'Work Category', visible: true, system: true },
    { _id: 'status', name: 'Stage Status', visible: true, system: true },
    { _id: 'allocationStatus', name: 'Allocation Status', visible: true, system: true },
    { _id: 'time', name: 'Time', visible: true, system: true },
    { _id: 'dueDate', name: 'Due Date', visible: false, system: true },
    // Visit fields render only on Visit-backed cards; hidden by default so a Job card's layout never changes.
    { _id: 'servicePlan', name: 'Service Plan', visible: false, system: true },
    { _id: 'serviceLine', name: 'Service Line', visible: false, system: true },
    { _id: 'visitWindow', name: 'Visit Window', visible: false, system: true },
    { _id: 'visitApproval', name: 'Approval Status', visible: false, system: true },
    { _id: 'visitPlanned', name: 'Planned Tag', visible: false, system: true },
    { _id: 'visitNumber', name: 'Visit Number', visible: false, system: true },
];
